import { Injectable } from '@angular/core';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { createSelector, select, Store } from '@ngrx/store';
import { OrderedSet, Map } from 'immutable';
import { map, Observable } from 'rxjs';

import { ConsumerMetadataItemEntity } from '../components/exposition-consumers-mapping/consumer-mapping-popup/consumer-metadata-item.entity';
import { ExpositionEndpointMetadataEntity } from '../components/exposition-endpoint-metadata-config/exposition-endpoint-metadata.entity';
import { ExpositionHistoryListEntity } from '../components/exposition-history-list/exposition-history-list.entity';
import { ExpositionMetadataEntity } from '../components/exposition-internal-metadata-config/exposition-metadata.entity';
import {
	ExpositionItemEntity,
	PublicationAction,
} from '../components/exposition-list/exposition-item.entity';
import { ExpositionListEntity } from '../components/exposition-list/exposition-list.entity';
import { MigrationReadyExpositionList } from '../components/exposition-migrations-list/exposition-migration-stepper/migration-ready-exposition-list.entity';
import { MigratedExpositionListEntity } from '../components/exposition-migrations-list/migrated-exposition-list.entity';
import { ExpositionDatablockListEntity } from '../components/exposition-stepper/exposition-datablock-list/exposition-datablock-list.entity';
import { ExpositionAccessListEntity } from '../components/exposition-view/exposition-access-list/exposition-access-list.entity';
import { ExpositionEntity } from '../components/exposition-view/exposition.entity';
import { PublishedExpositionEntity } from '../components/exposition-view/published-exposition/published-exposition.entity';
import { AccessConfigEntity } from '../domain/exposition-access-config.entity';
import { ExpositionColumnConfigListEntity } from '../domain/exposition-column-config-list.entity';
import { ExpositionConsumerMappingEntity } from '../domain/exposition-consumer-mapping.entity';
import { ExpositionConsumerEntity } from '../domain/exposition-consumer.entity';
import {
	ExpositionOriginalSliceState,
	ExpositionSliceKey,
} from './expositions.state';
import { ExpositionRootState, featureSelector } from './feature.store';

const baseSelector = createSelector(
	featureSelector,
	(state) => state[ExpositionSliceKey]
);

const eligibleConsumers = createSelector(baseSelector, (state) =>
	state.get('eligibleConsumers')
);

const consumerToMap = createSelector(baseSelector, (state) =>
	state.get('v1Consumers')
);

const allConsumers = createSelector(
	eligibleConsumers,
	(state) => state.consumers
);

const totalConsumers = createSelector(
	eligibleConsumers,
	(state) => state.total
);

@Injectable()
export class ExpositionsSelector {
	public constructor(private readonly store: Store<ExpositionRootState>) {}

	private get state(): Observable<ExpositionOriginalSliceState> {
		return this.store.pipe(select(baseSelector));
	}

	public getIsLoading$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('loading')));
	}

	public getActionInProgress$(): Observable<PublicationAction> {
		return this.state.pipe(map((state) => state.get('actionInProgress')));
	}

	public isSavingInProgress$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isSavingExposition')));
	}

	public getIsLoadingDatablockColumns$(): Observable<boolean> {
		return this.state.pipe(
			map((state) => state.get('isLoadingDatablockColumns'))
		);
	}

	public getColumnConfigList$(): Observable<ExpositionColumnConfigListEntity> {
		return this.state.pipe(map((state) => state.get('stepper').columns));
	}

	public getDatablockList$(): Observable<ExpositionDatablockListEntity> {
		return this.state.pipe(map((state) => state.get('stepper').datablocks));
	}

	public getAvailableTags$(): Observable<OrderedSet<AppTagItemEntity>> {
		return this.state.pipe(map((state) => state.get('availableTags')));
	}

	public getAvailableConsumers$(): Observable<Array<ExpositionConsumerEntity>> {
		return this.state.pipe(map((state) => state.get('availableConsumers')));
	}

	public getEligibleConsumers$(): Observable<
		Map<string, ConsumerMetadataItemEntity>
	> {
		return this.store.pipe(select(allConsumers));
	}

	public getTotalEligibleConsumers$(): Observable<number> {
		return this.store.pipe(select(totalConsumers));
	}

	public getDatablockLabel$(id: number): Observable<string> {
		return this.state.pipe(
			map(
				(state) => state.stepper.datablocks.elements.get(`${id}`)?.label ?? ''
			)
		);
	}

	public getExpositionView$(): Observable<ExpositionEntity> {
		return this.state.pipe(map((state) => state.get('inViewExposition')));
	}

	public getExpositionId$(): Observable<number> {
		return this.state.pipe(map((state) => state.get('inViewExposition').id));
	}

	public getColumns$(): Observable<ExpositionColumnConfigListEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').columns)
		);
	}
	public getAccessList$(): Observable<ExpositionAccessListEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').accessPoints)
		);
	}
	public getMetadata$(): Observable<ExpositionMetadataEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').metadata)
		);
	}
	public getEndpointMetadata$(): Observable<ExpositionEndpointMetadataEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').endpointMetadata)
		);
	}
	public getPublishedExposition$(): Observable<PublishedExpositionEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').published)
		);
	}
	public getAccessConfig$(): Observable<AccessConfigEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').accessPoints.currentConfig)
		);
	}

	public getExpositions$(): Observable<ExpositionListEntity> {
		return this.state.pipe(map((state) => state.get('expositions')));
	}

	public canCreateNewExposition$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('permissions').create));
	}

	public getCanDeleteExposition$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('permissions').delete));
	}

	public getCanEditExposition$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('permissions').edit));
	}

	public getExpositionsForMigration$(): Observable<MigrationReadyExpositionList> {
		return this.state.pipe(map((state) => state.get('v1Expositions')));
	}

	public getUnmappedConsumers$(): Observable<
		Array<ExpositionConsumerMappingEntity>
	> {
		return this.store.pipe(select(consumerToMap));
	}

	public getCurrentMappingRequestId$(): Observable<string> {
		return this.state.pipe(map((state) => state.get('mappingRequestId')));
	}

	public getMigratedExpositions$(): Observable<MigratedExpositionListEntity> {
		return this.state.pipe(map((state) => state.get('migrated')));
	}

	public getSelectedExpositions$(
		selected: Array<string>
	): Observable<Array<ExpositionItemEntity>> {
		return this.getExpositions$().pipe(
			map((list) =>
				selected.reduce<Array<ExpositionItemEntity>>((acc, curr) => {
					const maybe = list.elements.get(`${curr}`);
					if (maybe) {
						acc.push(maybe);
						return acc;
					}
					return acc;
				}, [])
			)
		);
	}

	public getHistory$(): Observable<ExpositionHistoryListEntity> {
		return this.state.pipe(map((state) => state.get('history')));
	}

	public getIsLoadingJobErrorLogs$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isLoadingJobErrorLogs')));
	}
	public getIsLoadingMetadataInfo$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isLoadingMetadataInfo')));
	}

	public hasConfiguredOpenAccess$(accessId: string): Observable<boolean> {
		return this.getExpositionView$().pipe(
			map((initExpositionView) =>
				initExpositionView.accessPoints.hasConfiguredOpenAccess(accessId)
			)
		);
	}
}
